<script>
  import { router } from '@inertiajs/svelte'
  import StoredInSpine from '../StoredInSpine/index.svelte'
  import SearchableSelect from '../SearchableSelect/index.svelte'

  // Move an item to a new location. Sequenced picker: property → space →
  // container. Choosing a container inherits its space (the server enforces
  // this too). Clearing both space and container leaves the item directly in
  // the property. Spaces/containers for the chosen property are fetched from
  // the item's #move_destinations endpoint.
  let {
    open = false,
    item,                 // { id, name, property_id, space_id, container_id, container_type, contained_count }
    onClose = () => {}
  } = $props()

  let propertyId = $state('')
  let spaceId = $state('')
  let containerId = $state('')
  let properties = $state([])  // destination properties, fetched on open
  let spaces = $state([])
  let containers = $state([])
  let loading = $state(false)
  let saving = $state(false)
  let error = $state('')

  const isContainer = $derived(!!item?.container_type)
  // Items directly inside this container. The item Show page sends
  // contained_count (whole subtree); list rows send item_count (direct items).
  const containedCount = $derived(item?.contained_count ?? item?.item_count ?? 0)
  // Direct child containers (list rows only). Lets the warning spell out both
  // "N items and M containers" instead of just the item count.
  const childContainerCount = $derived(item?.child_container_count ?? 0)

  // "4 items and 2 containers" / "3 items" / "1 container" — omit zero clauses.
  const movesAlongLabel = $derived.by(() => {
    const parts = []
    if (containedCount > 0) {
      parts.push(`${containedCount} ${containedCount === 1 ? 'item' : 'items'}`)
    }
    if (childContainerCount > 0) {
      parts.push(`${childContainerCount} ${childContainerCount === 1 ? 'container' : 'containers'}`)
    }
    return parts.join(' and ')
  })

  // Initialize once each time the modal transitions from closed → open. We
  // track wasOpen so this does NOT re-run when the user later changes the
  // property select (which would otherwise snap the selection back).
  let wasOpen = false
  $effect(() => {
    if (open && !wasOpen && item) {
      wasOpen = true
      propertyId = item.property_id || ''
      spaceId = item.space_id || ''
      containerId = item.container_id || ''
      error = ''
      loadDestinations(item.property_id, { keepSelection: true })
    } else if (!open) {
      wasOpen = false
    }
  })

  async function loadDestinations(pid, { keepSelection = false } = {}) {
    if (!pid || !item) return
    loading = true
    try {
      const res = await fetch(
        `/properties/${item.property_id}/items/${item.id}/move_destinations?target_property_id=${pid}`,
        { headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' } }
      )
      const data = await res.json()
      if (data.properties) properties = data.properties
      spaces = data.spaces || []
      containers = data.containers || []
      if (!keepSelection) {
        spaceId = ''
        containerId = ''
      }
    } catch (e) {
      error = 'Could not load destinations.'
    } finally {
      loading = false
    }
  }

  // When the destination property changes, reload its spaces/containers and
  // clear the (now-invalid) space/container selection.
  function onPropertyChange(v) {
    propertyId = v
    loadDestinations(propertyId, { keepSelection: false })
  }

  // Choosing a container inherits its space; the space select is then locked.
  function onContainerChange(v) {
    containerId = v
    const c = containers.find((x) => x.id === containerId)
    if (c) spaceId = c.space_id || ''
  }

  // Option lists for the searchable selects.
  const propertyOptions = $derived(properties.map((p) => ({ value: p.id, label: p.name })))
  const containerOptions = $derived(
    containers.map((c) => ({ value: c.id, label: c.label, sub: spaces.find((s) => s.id === c.space_id)?.name }))
  )
  const spaceOptions = $derived(spaces.map((s) => ({ value: s.id, label: s.name })))

  // Live "Stored in" preview of the chosen destination.
  const previewChain = $derived.by(() => {
    const prop = properties.find((p) => p.id === propertyId)
    const chain = [{ kind: 'property', label: prop?.name || '—', href: null, direct: false }]
    const container = containers.find((c) => c.id === containerId)
    const space = spaces.find((s) => s.id === (container ? container.space_id : spaceId))
    if (space) chain.push({ kind: 'space', label: space.name, href: null, direct: false })
    if (container) chain.push({ kind: 'container', label: container.label, href: null, direct: false })
    if (chain.length) chain[chain.length - 1].direct = true
    return chain
  })

  // Has anything actually changed from where the item is now?
  const changed = $derived(
    propertyId !== (item?.property_id || '') ||
    spaceId !== (item?.space_id || '') ||
    containerId !== (item?.container_id || '')
  )

  function close() {
    if (saving) return
    onClose()
  }

  function submit() {
    if (!changed || !item) { onClose(); return }
    saving = true
    error = ''
    router.patch(
      `/properties/${item.property_id}/items/${item.id}/move`,
      { move: { property_id: propertyId, space_id: spaceId, container_id: containerId } },
      {
        preserveScroll: true,
        onError: (errors) => {
          error = Object.values(errors)[0] || 'Could not move item.'
        },
        onFinish: () => { saving = false }
      }
    )
  }
</script>

{#if open}
  <div
    class="fixed inset-0 z-50 flex items-center justify-center p-5 bg-gray-900/55 backdrop-blur-[2px]"
    onclick={(e) => { if (e.target === e.currentTarget) close() }}
    role="presentation"
  >
    <div class="bg-white rounded-[20px] w-full max-w-[460px] shadow-2xl overflow-hidden">
      <!-- Head -->
      <div class="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100">
        <div>
          <div class="text-base font-extrabold text-gray-900">Move {item?.name}</div>
          <div class="text-[13px] text-gray-500 mt-0.5">Choose where this should live</div>
        </div>
        <button
          onclick={close}
          class="w-[30px] h-[30px] rounded-lg grid place-items-center border border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
          aria-label="Close"
        >
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
        </button>
      </div>

      <!-- Body -->
      <div class="p-5 space-y-4">
        {#if isContainer && movesAlongLabel}
          <div class="flex items-start gap-2.5 rounded-xl px-3.5 py-3 text-[13px] bg-amber-50 border border-amber-200 text-amber-800">
            <svg class="w-[18px] h-[18px] flex-shrink-0 mt-px" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
            <span>This is a container — moving it will also move the <b>{movesAlongLabel}</b> inside.</span>
          </div>
        {/if}

        <div>
          <span class="block text-sm font-medium text-gray-700 mb-1">Property</span>
          <SearchableSelect
            value={propertyId}
            options={propertyOptions}
            placeholder="Select a property"
            onChange={onPropertyChange}
          />
        </div>

        <div>
          <span class="block text-sm font-medium text-gray-700 mb-1">Container</span>
          <SearchableSelect
            value={containerId}
            options={containerOptions}
            placeholder="Select a container"
            noneLabel="No container (sits directly in the space)"
            disabled={loading}
            onChange={onContainerChange}
          />
        </div>

        <div>
          <span class="block text-sm font-medium text-gray-700 mb-1">Space</span>
          <SearchableSelect
            value={spaceId}
            options={spaceOptions}
            placeholder="Select a space"
            noneLabel="No space (directly in the property)"
            disabled={loading || !!containerId}
            onChange={(v) => (spaceId = v)}
          />
          {#if containerId}
            <p class="mt-1 text-xs text-gray-500">Inherited from the container</p>
          {/if}
        </div>

        <!-- Live destination preview -->
        <div>
          <div class="text-[11px] uppercase tracking-wide font-bold text-gray-500 mb-1.5">Will be stored in</div>
          <StoredInSpine chain={previewChain} />
        </div>

        {#if error}
          <p class="text-sm text-red-600">{error}</p>
        {/if}
      </div>

      <!-- Foot -->
      <div class="flex justify-end gap-2.5 px-5 py-3.5 border-t border-gray-100">
        <button
          type="button"
          onclick={close}
          class="text-sm font-semibold px-4 py-2.5 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onclick={submit}
          disabled={saving || !changed}
          class="text-sm font-semibold px-4 py-2.5 rounded-lg bg-brand-700 text-white hover:bg-brand-800 disabled:opacity-60"
        >
          {saving ? 'Moving…' : 'Move here'}
        </button>
      </div>
    </div>
  </div>
{/if}
